import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import { BatchExecutor } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'

const useInvalidateCloudPricingstate = () => {
  const queryClient = useQueryClient()

  return {
    invalidate: () =>
      queryClient.invalidateQueries({ queryKey: ['batch-executor', 'cloud-pricing', 'state'] }),
  }
}

export const useStartCloudPricingMutation = () => {
  const api = useApi()

  const { invalidate } = useInvalidateCloudPricingstate()

  return useMutation({
    mutationFn: () => {
      return api.batchExecutor.cloudPricing.start()
    },
    onSuccess: () => {
      toast.success('Cloud Pricing started')
    },
    onSettled: () => {
      invalidate()
    },
  })
}

export const useStopCloudPricingMutation = () => {
  const api = useApi()

  const { invalidate } = useInvalidateCloudPricingstate()

  return useMutation({
    mutationFn: () => {
      return api.batchExecutor.cloudPricing.stop()
    },
    onSuccess: () => {
      toast.success('Cloud Pricing stopped')
    },
    onSettled: () => {
      invalidate()
    },
  })
}

export const useRebootCloudPricingMutation = () => {
  const api = useApi()

  const { invalidate } = useInvalidateCloudPricingstate()

  return useMutation({
    mutationFn: () => {
      return api.batchExecutor.cloudPricing.reboot()
    },
    onSuccess: () => {
      toast.success('Cloud Pricing rebooted')
    },
    onSettled: () => {
      invalidate()
    },
  })
}

export const useRunCustomStrategiesMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: BatchExecutor.RunCustomStrategiesParams) => {
      return api.batchExecutor.cloudPricing.runCustomStrategies(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Started running Custom Strategies')
    },
  })
}
export const useRunVoldivAdjustmentsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: BatchExecutor.RunVoldivAdjustmentsParams) => {
      return api.batchExecutor.cloudPricing.runVoldivAdjustments(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Started running Vol-Div Adjustments')
    },
  })
}
export const useRunCalibrationMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: BatchExecutor.RunCalibrationParams) => {
      return api.batchExecutor.cloudPricing.runCalibration(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Started running Calibration')
    },
  })
}
export const useRunDefaultRiskMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: BatchExecutor.RunDefaultRiskParams) => {
      return api.batchExecutor.cloudPricing.runDefaultRisk(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Started Default Risk for ${_params.funds.join(', ')}`)
    },
    onError: (error, _params) => {
      toastApiError(error, `Error starting Default Risk for ${_params.funds.join(', ')}`)
    },
  })
}
export const useRunCustomRiskMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: BatchExecutor.RunCustomRiskParams) => {
      return api.batchExecutor.cloudPricing.runCustomRisk(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Custom Risk started')
    },
  })
}

export const useSendBearScenarioEmailMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (params: BatchExecutor.SendBearScenarioEmailParams) => {
      return api.batchExecutor.sendBearScenarioEmail(params)
    },
    onSuccess: (_data, _params) => {
      toast.success('Running bear scenario', {
        description: 'Expect the email within a few minutes',
      })
    },
  })
}

export const useRollRiskMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (params: { date_t: string; date_tmo: string }) => {
      return api.batchExecutor.rollRisk(params)
    },
    onSuccess: (_data, _params) => {
      toast.success('Risk rolled successfully')
    },
    onError: (error, _params) => {
      toastApiError(
        error,
        'Error rolling risk, check that data exists for T-1 before rolling risk.',
      )
    },
  })
}

export const useRunAllDefaultJobsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (params: BatchExecutor.RunAllDefaultJobsParams) => {
      return api.batchExecutor.cloudPricing.runAllDefaultJobs(params)
    },
    onSuccess: (_data, _params) => {
      toast.success('All EOD Risk Jobs started')
    },
    onError: (error, _params) => {
      toastApiError(error, 'An error ocurred while attempting to run all default jobs.')
    },
  })
}

export const useRunDerivedMetricsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (params: BatchExecutor.RunDerivedMetricsParams) => {
      return api.batchExecutor.cloudPricing.runDerivedMetrics(params)
    },
    onSuccess: (_data, _params) => {
      toast.success('Derived Metrics started')
    },
    onError: (error, _params) => {
      toastApiError(error, 'An error ocurred while attempting to run Derived Metrics.')
    },
  })
}
